import { MachineContext } from 'contexts/machine.context';
import { QUEUE_STATES_THAT_ALLOW_AUTO_FIRE } from 'lib_ts/enums/machine-msg.enum';
import {
  MACHINE_STATUSES_THAT_ALLOW_AUTO_FIRE,
  MachineStatus,
} from 'lib_ts/enums/machine.enums';
import { useContext, useEffect, useRef } from 'react';
import useMachineStatusStore from 'stores/use-machine-status-store';

const CONTEXT_NAME = 'useAutoFireSafetyListener';

// disables auto-fire automatically when specific events are triggered
const useAutoFireSafetyListener = (): void => {
  const { setAutoFire } = useContext(MachineContext);

  const queueState = useMachineStatusStore((s) => s.queueState);

  // immediately disable auto-fire if queue state is not allowed for auto-fire
  useEffect(() => {
    if (QUEUE_STATES_THAT_ALLOW_AUTO_FIRE.includes(queueState)) {
      return;
    }

    console.warn(
      `${CONTEXT_NAME}: queue state > ${queueState} > disabling auto-fire`
    );
    setAutoFire(false);
  }, [queueState, setAutoFire]);

  // after first encountering a non-allowed machine status, wait a brief delay before disabling auto-fire
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const r2fMachineStatus = useMachineStatusStore(
    (s) => s.r2fData?.machine_status
  );

  // after a brief delay, disable auto-fire if machine status from R2F is not allowed for auto-fire
  useEffect(() => {
    if (!r2fMachineStatus) {
      // do nothing for unknown status
      return;
    }

    if (MACHINE_STATUSES_THAT_ALLOW_AUTO_FIRE.includes(r2fMachineStatus)) {
      if (!timeoutRef.current) {
        // no timeout to clear
        return;
      }

      // clear any existing timeout to prevent the AF disable from happening
      clearTimeout(timeoutRef.current);
      timeoutRef.current = undefined;
      return;
    }

    // handle bad statuses
    switch (r2fMachineStatus) {
      case MachineStatus.NO_BALL: {
        if (timeoutRef.current) {
          // do not disturb an existing timeout that's counting down
          return;
        }

        // allow for a grace period when entering no ball status, in case it fixes itself shortly after
        timeoutRef.current = setTimeout(() => {
          // cleanup
          timeoutRef.current = undefined;

          // disable auto-fire after delay
          console.warn(
            `${CONTEXT_NAME}: machine status > ${r2fMachineStatus} > disabling auto-fire after grace period`
          );

          setAutoFire(false);
        }, 5_000);
        return;
      }

      default: {
        // clear any existing timeout to prevent the AF disable from happening an extra time
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = undefined;
        }

        // disable auto-fire immediately
        console.warn(
          `${CONTEXT_NAME}: machine status > ${r2fMachineStatus} > disabling auto-fire immediately`
        );

        setAutoFire(false);
        return;
      }
    }
  }, [r2fMachineStatus, setAutoFire]);
};

export default useAutoFireSafetyListener;
